import { StopOutlined } from '@ant-design/icons'
import { Button, Select, Tag, Tooltip } from 'antd'
import { useSelector } from 'react-redux'
import { UserAvatar } from '../../pages/OpenChannels/userAvatar'
import { Organization } from '../../reducers/organizationReducer'
import { AxoloDeactivateIcon, AxoloArchiveIcon } from '../Settings/setting-helper'
import { findTeamNameFromId } from '../TeamChannels/helpers'
import { dedupeByStableId } from '../TeamChannels/selectorUtils'
import {
  classActionColumn,
  classChannelColumn,
  classCreatorColumn,
  classTeamColumn,
} from './teamChannelTableHelper'
import { useAxiosWithHeader, globalUpdateCompanySetting } from '../../utils'
import { useDispatch } from 'react-redux'
import messageInteraction from '../Analytics/utils/messageInteraction'

const { Option } = Select

export async function reactivateDisabledChannel(channel, setting, axios, dispatch) {
  try {
    const oldChannels = setting.channels
    const newChannels = oldChannels.map((c) => {
      if (c.id === channel.id) {
        return { ...c, desactivate: false }
      }
      return c
    })

    await globalUpdateCompanySetting({
      key: 'channels',
      value: newChannels,
      setting,
      axios,
      dispatch,
    })
  } catch (e) {
    console.log('Error reactivating channel.' + e.message)
  }
}

export const TeamChannelTableData = ({
  handleSelectTeamsOnExistingChannel,
  newChannelState,
  validateTeams,
  onDelete,
  channels,
  personal,
}) => {
  const organization = useSelector(Organization.selectors.getOrganization)
  const { teams = [], setting } = organization
  const uniqueTeams = dedupeByStableId(
    teams,
    (team) => team?.providerId || team?.id,
    (team) => team?.name,
  )
  const axios = useAxiosWithHeader()
  const dispatch = useDispatch()

  const TeamTags = ({ channel }) => {
    let disable
    const raw = channel
    const teamSelect = []
    const preselected = channel?.teams?.map((id) => {
      const teamName = findTeamNameFromId({ id, teams })
      return teamName
    })

    if (!personal || preselected.includes('All Axolo onboarded users')) {
      const teamNames = channel?.teams?.map((id) => (
        <Tag key={id} className="font-medium" color="blue">
          {findTeamNameFromId({ id, teams })}
        </Tag>
      ))
      return teamNames
    }

    for (let i = 0; i < uniqueTeams.length; i++) {
      const team = uniqueTeams[i]
      teamSelect.push(
        <Option key={team.id} value={team.name} id={team.id}>
          {team.name}
        </Option>,
      )
    }

    return (
        <div className="flex w-full flex-col gap-1">
        <Select
          mode="multiple"
          allowClear
          disabled={disable}
          size="small"
          style={{ width: '100%', minWidth: '120px', borderColor: '#0049ff', fontSize: '11px' }}
          placeholder="Select team"
          defaultValue={preselected}
          labelInValue
          onChange={(e) => handleSelectTeamsOnExistingChannel(e, raw)}
          tagRender={(props) => {
            const { label, closable, onClose } = props
            return (
              <Tag
                color="blue"
                closable={closable}
                onClose={onClose}
                style={{ marginRight: 3, display: 'flex', alignItems: 'center' }}
              >
                {label}
              </Tag>
            )
          }}
        >
          {teamSelect}
        </Select>
        <Button
          size="small"
          onClick={validateTeams}
          disabled={!newChannelState || newChannelState?.id !== raw?.id}
          style={{ width: 'fit-content' }}
        >
          Validate
        </Button>
      </div>
    )
  }

  const RepositoriesTag = ({ channel }) => {
    if (!(channel?.repositories?.length > 0)) {
      return <Tag color="green">All repositories</Tag>
    }
    return (
      <div className="mr-2 border p-1">
        {channel?.repositories?.map((repo) => (
          <Tag key={repo} className="font-medium" color="green">
            {repo}
          </Tag>
        ))}
      </div>
    )
  }

  const NotificationTriggerCell = ({ channel }) => {
    const options = [
      { value: 'team_membership', label: 'Member creates PR' },
      { value: 'team_assignment', label: 'Team assigned' },
    ]

    const handleChange = async (value) => {
      try {
        const hide = messageInteraction({
          content: 'Updating...',
          type: 'loading',
          duration: 0,
        })

        const updatedChannel = { ...channel, notificationTrigger: value }
        const updatedSettingsResp = await axios.put(`/settings/${setting._id}`, {
          channel: updatedChannel,
          updatingTeamsInSpecificChannel: true,
        })

        if (hide) hide()

        if (updatedSettingsResp.status === 200) {
          messageInteraction({
            content: 'Notification trigger updated.',
            type: 'success',
          })
          dispatch(Organization.actions.setData({ setting: updatedSettingsResp.data }))
        }
      } catch (error) {
        console.log('Error updating notification trigger', error)
        messageInteraction({
          content: "Couldn't update. Try again or contact support.",
          type: 'error',
        })
      }
    }

    if (!personal) {
      const displayValue = channel.notificationTrigger || 'team_membership'
      const option = options.find((o) => o.value === displayValue)
      return (
        <div className="text-xs text-gray-600">
          <Tag color="blue" className="text-xs">
            {option?.label}
          </Tag>
        </div>
      )
    }

    return (
      <Select
        value={channel.notificationTrigger || 'team_membership'}
        onChange={handleChange}
        options={options}
        size="small"
          style={{ width: '180px', fontSize: '11px' }}
      />
    )
  }

  const CellAction = ({ channel }) => {
    if (!personal)
      return (
        <>
          <Tooltip title="You can't take actions on channels settings that are not yours.">
            <Button
              icon={<StopOutlined />}
              shape="circle"
              style={{ color: 'grey' }}
              className="h-8 w-8"
            />
          </Tooltip>
        </>
      )
    if (!channel.desactivate) {
      return (
        <>
          <Tooltip placement="topRight" title="Archive this channel">
            <div
              className="cursor-pointer"
              onClick={() => onDelete({ channelTitle: channel.title })}
            >
              <AxoloArchiveIcon size={8} color={'red'} />
            </div>
          </Tooltip>
          <br></br>
          <Tooltip
            placement="topRight"
            title="Disable Axolo on this channel"
            className="ml-3"
          >
            <div
              className="cursor-pointer"
              onClick={() => onDelete({ channelTitle: channel.title, desactivate: true })}
            >
              <AxoloDeactivateIcon color={'#faad14'} size={8} />
            </div>
          </Tooltip>
        </>
      )
    } else {
      let textDisabled =
        'This channel has been disabled, upgrade to business to reactivate it.'
      const isBusinessPlan = organization.plan === 'business'
      if (isBusinessPlan) {
        textDisabled = 'Reactivate this channel by clicking on the icon.'
      }
      return (
        <>
          <Tooltip title={textDisabled}>
            <Button
              icon={<StopOutlined />}
              shape="circle"
              style={{ color: 'grey' }}
              className="h-8 w-8"
              onClick={() =>
                isBusinessPlan
                  ? reactivateDisabledChannel(channel, setting, axios, dispatch)
                  : null
              }
            />
          </Tooltip>
        </>
      )
    }
  }

  if (channels === null || channels === undefined || channels?.length === 0) {
    return null
  }

  return channels?.map((channel) => (
    <tr key={channel?.id}>
      <td className={classChannelColumn}>{channel?.title}</td>

      {organization?.provider === 'github' && (
        <td className="text-xs text-gray-600">
          Channel is based
          {!channel.option || channel.option === 'on team' ? (
            <Tag color="blue" className="!ml-1">
              on team
            </Tag>
          ) : (
            <Tag color="green" className="!ml-1">
              {channel.option}
            </Tag>
          )}
        </td>
      )}
      <td className={classTeamColumn}>
        <RepositoriesTag channel={channel} />
        <TeamTags channel={channel} personal={personal} />
      </td>
      {organization?.provider === 'github' && (
        <td className="px-2 py-2">
          <NotificationTriggerCell channel={channel} />
        </td>
      )}
      <td className={classCreatorColumn}>
        <div className="flex flex-col items-start">
          <UserAvatar record={channel?.creator} size="small" />
          <p className="mb-0 mt-1 w-full truncate text-xs text-gray-800">
            {channel?.creator?.providerLogin}
          </p>
        </div>
      </td>
      <td className={classActionColumn}>
        <span href="#" className="text-indigo-600 hover:text-indigo-900">
          <div className="flex justify-center ">
            <CellAction channel={channel} />
          </div>
        </span>
      </td>
    </tr>
  ))
}
